// src/components/APITester.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AnimatedBackground } from './ui/AnimatedBackground';

const APITester = () => {
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('https://jsonplaceholder.typicode.com/posts');
  const [response, setResponse] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState([{ key: 'Content-Type', value: 'application/json' }]);
  const [body, setBody] = useState('{\n  "title": "test",\n  "body": "test body",\n  "userId": 1\n}');

  const methods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const updateHeader = (index, field, value) => {
    const updatedHeaders = [...headers];
    updatedHeaders[index][field] = value;
    setHeaders(updatedHeaders);
  };

  const removeHeader = (index) => {
    const updatedHeaders = headers.filter((_, i) => i !== index);
    setHeaders(updatedHeaders);
  };

  const testAPI = async () => {
    setLoading(true);
    setResponse('');
    setStatus('');

    try {
      const headersObj = {};
      headers.forEach(header => {
        if (header.key && header.value) {
          headersObj[header.key] = header.value;
        }
      });

      const options = {
        method,
        headers: headersObj,
      };

      if (['POST', 'PUT', 'PATCH'].includes(method) && body) {
        options.body = body;
      }

      const startTime = Date.now();
      const res = await fetch(url, options);
      const endTime = Date.now();
      const responseTime = endTime - startTime;

      const data = await res.text();

      setStatus(`${res.status} ${res.statusText} • ${responseTime}ms`);
      setResponse(data);

    } catch (error) {
      setStatus(`Error: ${error.message}`);
      setResponse(error.toString());
    } finally {
      setLoading(false);
    }
  };

  const formatJSON = () => {
    try {
      const parsed = JSON.parse(response);
      setResponse(JSON.stringify(parsed, null, 2));
    } catch (e) {
      // Not JSON, do nothing
    }
  };

  const copyResponse = () => {
    navigator.clipboard.writeText(response);
  };

  const clearAll = () => {
    setResponse('');
    setStatus('');
    setUrl('https://jsonplaceholder.typicode.com/posts');
    setBody('{\n  "title": "test",\n  "body": "test body",\n  "userId": 1\n}');
    setHeaders([{ key: 'Content-Type', value: 'application/json' }]);
  };

  return (
    <>
      <AnimatedBackground />
      
      <Section background="gradient" spacing={4}>
        <Container maxWidth="lg">
          <br/><br/><br/>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h1" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 2,
              }}
            >
              API Tester
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                mb: 4,
              }}
            >
              Test any API endpoint like Postman
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Request Section */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                border: '1px solid rgba(139, 0, 0, 0.3)',
                height: '100%',
              }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d', mb: 3 }}>
                    Request
                  </Typography>

                  {/* Method and URL */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <FormControl sx={{ minWidth: 120 }}>
                      <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Method</InputLabel>
                      <Select
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                        sx={{
                          color: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(139, 0, 0, 0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(139, 0, 0, 0.5)',
                          },
                        }}
                      >
                        {methods.map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Input
                      label="URL"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      fullWidth
                      placeholder="https://api.example.com/endpoint"
                    />
                  </Box>

                  {/* Headers */}
                  <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                    Headers
                  </Typography>
                  
                  {headers.map((header, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Input
                        placeholder="Key"
                        value={header.key}
                        onChange={(e) => updateHeader(index, 'key', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Input
                        placeholder="Value"
                        value={header.value}
                        onChange={(e) => updateHeader(index, 'value', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <IconButton 
                        onClick={() => removeHeader(index)}
                        sx={{ color: '#ff4d4d' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    variant="outlined"
                    onClick={addHeader}
                    startIcon={<AddIcon />}
                    sx={{ mb: 3 }}
                  >
                    Add Header
                  </Button>

                  {/* Body */}
                  {['POST', 'PUT', 'PATCH'].includes(method) && (
                    <>
                      <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                        Body
                      </Typography>
                      <TextField
                        multiline
                        rows={6}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': {
                              borderColor: 'rgba(139, 0, 0, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(139, 0, 0, 0.5)',
                            },
                          },
                        }}
                      />
                    </>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={testAPI}
                      loading={loading}
                      startIcon={<PlayArrowIcon />}
                      sx={{ flex: 1 }}
                    >
                      Send Request
                    </Button>
                    
                    <Button
                      variant="outlined"
                      onClick={clearAll}
                    >
                      Clear
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Response Section */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                border: '1px solid rgba(139, 0, 0, 0.3)',
                height: '100%',
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ color: '#ff4d4d' }}>
                      Response
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={formatJSON}
                        disabled={!response}
                      >
                        Format JSON
                      </Button>
                      
                      <IconButton 
                        onClick={copyResponse}
                        disabled={!response}
                        sx={{ color: '#ff4d4d' }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Status */}
                  {status && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: status.includes('Error') ? '#ff4444' : '#44ff44',
                        mb: 2,
                        fontFamily: 'monospace',
                      }}
                    >
                      {status}
                    </Typography>
                  )}

                  {/* Response Body */}
                  <TextField
                    multiline
                    rows={15}
                    value={response}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        '& fieldset': {
                          borderColor: 'rgba(139, 0, 0, 0.3)',
                        },
                      },
                    }}
                  />

                  {!response && !loading && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.5)',
                        textAlign: 'center',
                        mt: 4,
                      }}
                    >
                      Send a request to see the response here
                    </Typography>
                  )}

                  {loading && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#ff4d4d',
                        textAlign: 'center',
                        mt: 4,
                      }}
                    >
                      Sending request...
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Examples */}
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
            border: '1px solid rgba(139, 0, 0, 0.3)',
            mt: 4,
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#ff4d4d' }}>
                Quick Test Examples
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setMethod('GET');
                      setUrl('https://jsonplaceholder.typicode.com/posts');
                    }}
                  >
                    JSONPlaceholder GET
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setMethod('POST');
                      setUrl('https://jsonplaceholder.typicode.com/posts');
                      setBody('{\n  "title": "test",\n  "body": "test body",\n  "userId": 1\n}');
                    }}
                  >
                    JSONPlaceholder POST
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setMethod('GET');
                      setUrl('https://api.github.com/users/octocat');
                    }}
                  >
                    GitHub API
                  </Button>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setMethod('GET');
                      setUrl('https://catfact.ninja/fact');
                    }}
                  >
                    Cat Facts API
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Section>
    </>
  );
};

export default APITester;